/**
 * Drop Code on screen. The engine (engine.ts) decides right and wrong; this file turns pad
 * presses into directions, animates the trooper, and keeps the clock honest.
 *
 * Inputs
 *   the four-way pad → that direction, read on pointerdown (this game is pure speed; there
 *                      is no hold or double-tap to disambiguate against, so nothing waits)
 *   keys: arrows or WASD · esc pause
 *
 * The trooper is a photograph on a black background with no alpha channel — his armour is
 * black too, so keying it out eats the suit. `mix-blend-mode: lighten` over the app's dark
 * panel drops the background and keeps the armour, which is why he only ever stands on dark.
 */
import { DropCode, ARROW, type Call, type Dir, type Outcome, formatTime } from './engine';
import type { GameContext } from '../registry';
import { el, toast } from '../../ui/dom';
import { showResult } from '../../ui/result';
import { dropCodeDef } from './def';

const KEYS: Record<string, Dir> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  w: 'up',
  s: 'down',
  a: 'left',
  d: 'right',
};

const PAD: { dir: Dir; label: string; cell: string }[] = [
  { dir: 'up', label: ARROW.up, cell: 'u' },
  { dir: 'left', label: ARROW.left, cell: 'l' },
  { dir: 'down', label: ARROW.down, cell: 'd' },
  { dir: 'right', label: ARROW.right, cell: 'r' },
];

export function mountDropCode(ctx: GameContext): () => void {
  const { root, sfx, buzz, player } = ctx;
  const game = new DropCode();
  let raf = 0;
  let running = false;
  let lastClock = '';
  let shownCall: Call | null = null;
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const later = (fn: () => void, ms: number) => {
    const t = setTimeout(() => {
      timers.delete(t);
      fn();
    }, ms);
    timers.add(t);
    return t;
  };

  // ---- DOM -----------------------------------------------------------------
  const clockEl = el('div', { class: 'dc-clock' }, '0.0s');
  const progressEl = el('div', { class: 'dc-progress' }, '0/10');
  const mistakesEl = el('div', { class: 'dc-mist', title: 'mistakes' }, '✕ 0');
  const windowFill = el('div', { class: 'fill' });
  const trooper = el('img', {
    class: 'dc-trooper',
    src: `${import.meta.env.BASE_URL}dropcode/trooper.jpg`,
    alt: 'A trooper in full armour, standing and waiting on a call-in',
    draggable: 'false',
  });
  const field = el('div', { class: 'dc-field' }, trooper);
  const callName = el('div', { class: 'dc-name' }, 'Stand by');
  const codeEl = el('div', { class: 'dc-code' });
  const callEl = el('div', { class: 'dc-call' }, callName, codeEl);
  const padBtns = PAD.map((p) =>
    el('button', { class: `dc-key dc-${p.cell}`, type: 'button', 'data-dir': p.dir, 'aria-label': p.dir }, p.label),
  );
  const pad = el('div', { class: 'dc-pad' }, ...padBtns);
  const overlay = el('div', { class: 'ts-overlay' });
  const wrap = el(
    'section',
    { class: 'dc' },
    el(
      'header',
      { class: 'dc-top' },
      el('button', { class: 'icon-btn', type: 'button', 'aria-label': 'pause', onclick: () => togglePause() }, '❚❚'),
      clockEl,
      progressEl,
      mistakesEl,
    ),
    el('div', { class: 'dc-window' }, windowFill),
    field,
    callEl,
    pad,
    overlay,
  );
  root.append(wrap);

  // ---- input ---------------------------------------------------------------
  for (const btn of padBtns) {
    const dir = btn.getAttribute('data-dir') as Dir;
    // pointerdown: nothing here needs disambiguating, so nothing waits
    btn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      flash(btn);
      press(dir);
    });
    btn.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  function flash(btn: HTMLElement): void {
    btn.classList.remove('hit');
    void btn.offsetWidth;
    btn.classList.add('hit');
  }

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') return togglePause();
    if (e.repeat || !running) return;
    const dir = KEYS[e.key];
    if (!dir) return;
    e.preventDefault();
    const btn = padBtns.find((b) => b.getAttribute('data-dir') === dir);
    if (btn) flash(btn);
    press(dir);
  }
  window.addEventListener('keydown', onKey);

  function press(dir: Dir): void {
    if (!running || game.paused) return;
    const out = game.input(dir, performance.now());
    if (out) resolve(out);
  }

  // ---- painting ------------------------------------------------------------
  function paintCall(): void {
    const call = game.call;
    if (!call) return;
    if (call !== shownCall) {
      shownCall = call;
      callName.replaceChildren(el('span', { class: 'ic' }, call.icon), el('span', {}, call.name));
      codeEl.replaceChildren(...call.code.map((d) => el('span', { class: 'a' }, ARROW[d])));
      callEl.classList.remove('in');
      void callEl.offsetWidth;
      callEl.classList.add('in');
    }
    const at = game.progress;
    codeEl.childNodes.forEach((node, i) => {
      const s = node as HTMLElement;
      s.classList.toggle('done', i < at);
      s.classList.toggle('now', i === at);
    });
    progressEl.textContent = `${game.landed}/${game.rules.calls}`;
  }

  function resolve(out: Outcome): void {
    mistakesEl.textContent = `✕ ${game.mistakes}`;
    progressEl.textContent = `${game.landed}/${game.rules.calls}`;
    if (out.ok && !out.landed) {
      sfx.tick();
      buzz.light();
      paintCall();
      return;
    }
    if (out.landed) {
      sfx.slap();
      buzz.success();
      field.classList.remove('land');
      void field.offsetWidth;
      field.classList.add('land');
      toast(wrap, `${out.call.icon} ${out.call.name} away`, 'ok', 900);
    } else {
      sfx.buzz();
      buzz.error();
      field.classList.remove('bad');
      void field.offsetWidth;
      field.classList.add('bad');
      codeEl.classList.remove('reset');
      void codeEl.offsetWidth;
      codeEl.classList.add('reset');
      if (out.did === 'timeout') toast(wrap, `${out.call.name} lost — too slow`, 'bad', 1100);
      else toast(wrap, `Wrong — that code starts ${ARROW[out.call.code[0]]}`, 'bad', 1100);
    }
    if (out.won) {
      running = false;
      later(() => void finish(), 700);
      return;
    }
    paintCall();
  }

  // ---- loop ----------------------------------------------------------------
  function frame(now: number): void {
    if (!running) return;
    const out = game.tick(now);
    if (out) resolve(out);
    if (!running) return;
    const clock = formatTime(game.elapsed(now));
    if (clock !== lastClock) {
      clockEl.textContent = clock;
      lastClock = clock;
    }
    const r = game.remaining(now);
    windowFill.style.transform = `scaleX(${r.toFixed(3)})`;
    windowFill.classList.toggle('low', r < 0.3);
    raf = requestAnimationFrame(frame);
  }

  // ---- start / pause / finish ----------------------------------------------
  function sheet(...kids: (Node | string)[]): void {
    overlay.replaceChildren(el('div', { class: 'sheet' }, ...kids));
    overlay.classList.add('show');
  }

  function ready(): void {
    sheet(
      el('h2', {}, 'Drop Code'),
      el('p', {}, 'A call-in comes up with a code. Punch the arrows in before the window shuts.'),
      el('p', { class: 'dim' }, 'Ten of them. Same codes every time, so they can be learned.'),
      el('button', { class: 'btn primary big', type: 'button', onclick: () => countdown() }, 'Start'),
    );
  }

  function countdown(): void {
    sfx.unlock();
    let n = 3;
    const big = el('div', { class: 'count' }, String(n));
    overlay.replaceChildren(big);
    const step = () => {
      n--;
      if (n <= 0) {
        overlay.classList.remove('show');
        overlay.replaceChildren();
        start();
        return;
      }
      big.textContent = String(n);
      big.classList.remove('pop');
      void big.offsetWidth;
      big.classList.add('pop');
      sfx.tick();
      later(step, 600);
    };
    sfx.tick();
    later(step, 600);
  }

  function start(): void {
    game.start(performance.now());
    running = true;
    lastClock = '';
    shownCall = null;
    mistakesEl.textContent = '✕ 0';
    paintCall();
    raf = requestAnimationFrame(frame);
  }

  function togglePause(): void {
    if (!running) return;
    if (game.paused) {
      game.resume(performance.now());
      overlay.classList.remove('show');
      overlay.replaceChildren();
      raf = requestAnimationFrame(frame);
    } else {
      game.pause(performance.now());
      cancelAnimationFrame(raf);
      sheet(
        el('h2', {}, 'Paused'),
        el('button', { class: 'btn primary big', type: 'button', onclick: () => togglePause() }, 'Resume'),
        el('button', { class: 'btn ghost', type: 'button', onclick: () => ctx.exit() }, 'Menu'),
      );
    }
  }

  function restart(): void {
    running = false;
    cancelAnimationFrame(raf);
    overlay.classList.remove('show');
    overlay.replaceChildren();
    countdown();
  }

  async function finish(): Promise<void> {
    running = false;
    cancelAnimationFrame(raf);
    const score = Math.round(game.elapsed(performance.now()));
    clockEl.textContent = formatTime(score);
    windowFill.style.transform = 'scaleX(0)';
    sfx.win();
    buzz.success();
    const result = {
      score,
      mistakes: game.mistakes,
      detail: { bestStreak: game.bestStreak, landed: game.landed, rules: game.rules },
      practice: player.practice,
    };
    const { board, newBest } = await ctx.finish(result);
    showResult(overlay, {
      game: dropCodeDef,
      result,
      board,
      newBest,
      player,
      post: () => ctx.post(result),
      again: () => restart(),
      board_: () => ctx.showBoard(),
      menu: () => ctx.exit(),
    });
  }

  function onHidden(): void {
    if (document.hidden && running && !game.paused) togglePause();
  }
  document.addEventListener('visibilitychange', onHidden);

  ctx.setBackHandler(() => {
    if (running && !game.paused) {
      togglePause();
      return true;
    }
    return false;
  });

  ready();

  return () => {
    running = false;
    cancelAnimationFrame(raf);
    for (const t of timers) clearTimeout(t);
    window.removeEventListener('keydown', onKey);
    document.removeEventListener('visibilitychange', onHidden);
    ctx.setBackHandler(null);
    wrap.remove();
  };
}
